import './RuleBuilder.css'

export const EMPTY_RULE = { text: '', caseSensitive: false, wholeWords: false }

// A single AND/OR switch, styled like the library filter's Any/All toggle.
function LogicToggle({ value, onChange, anyLabel, allLabel }) {
  return (
    <div className="ct-toggle ts-logic" role="group">
      <button
        type="button"
        className={'ct-toggle-btn' + (value === 'or' ? ' is-active' : '')}
        onClick={() => onChange('or')}
      >
        {anyLabel}
      </button>
      <button
        type="button"
        className={'ct-toggle-btn' + (value === 'and' ? ' is-active' : '')}
        onClick={() => onChange('and')}
      >
        {allLabel}
      </button>
    </div>
  )
}

function RuleRow({ rule, onChange, onRemove, placeholder }) {
  return (
    <div className="ts-rule">
      <input
        className="ct-input ts-rule-text"
        type="text"
        value={rule.text}
        placeholder={placeholder}
        onChange={(event) => onChange({ ...rule, text: event.target.value })}
      />
      <button
        type="button"
        className={'ts-rule-flag' + (rule.caseSensitive ? ' is-active' : '')}
        title="Match case"
        aria-pressed={rule.caseSensitive}
        onClick={() => onChange({ ...rule, caseSensitive: !rule.caseSensitive })}
      >
        Aa
      </button>
      <button
        type="button"
        className={'ts-rule-flag' + (rule.wholeWords ? ' is-active' : '')}
        title="Whole words only"
        aria-pressed={rule.wholeWords}
        onClick={() => onChange({ ...rule, wholeWords: !rule.wholeWords })}
      >
        W
      </button>
      <button
        type="button"
        className="ts-rule-remove"
        title="Remove rule"
        aria-label="Remove rule"
        onClick={onRemove}
      >
        &times;
      </button>
    </div>
  )
}

function RuleGroup({ title, hint, logic, onLogicChange, rules, onRulesChange, anyLabel, allLabel, placeholder }) {
  const setRule = (index, next) => {
    onRulesChange(rules.map((r, i) => (i === index ? next : r)))
  }
  const removeRule = (index) => {
    onRulesChange(rules.filter((_, i) => i !== index))
  }
  const addRule = () => onRulesChange([...rules, { ...EMPTY_RULE }])

  return (
    <section className="ts-group">
      <div className="ts-group-head">
        <div className="ts-group-titles">
          <span className="ts-group-title">{title}</span>
          {hint ? <span className="ts-group-hint">{hint}</span> : null}
        </div>
        {rules.length > 1 ? (
          <LogicToggle value={logic} onChange={onLogicChange} anyLabel={anyLabel} allLabel={allLabel} />
        ) : null}
      </div>

      {rules.length === 0 ? (
        <p className="ts-group-empty">No rules.</p>
      ) : (
        <div className="ts-rules">
          {rules.map((rule, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <RuleRow
              key={index}
              rule={rule}
              placeholder={placeholder}
              onChange={(next) => setRule(index, next)}
              onRemove={() => removeRule(index)}
            />
          ))}
        </div>
      )}

      <button type="button" className="ts-add-rule" onClick={addRule}>
        + Add rule
      </button>
    </section>
  )
}

// Generic rule builder (Find + Exempt) used by both Global Text Search and the
// in-PDF search popover. The caller owns the `draft` state and receives every
// change via `onChange`. `extraActions` renders additional buttons beside Run.
function RuleBuilder({ draft, onChange, onRun, running, extraActions }) {
  const patch = (changes) => onChange({ ...draft, ...changes })
  const canRun = draft.findRules.some((r) => r.text.trim().length > 0)

  return (
    <div className="ts-builder">
      <RuleGroup
        title="Find"
        hint="pages that match"
        logic={draft.findRuleLogic}
        onLogicChange={(value) => patch({ findRuleLogic: value })}
        rules={draft.findRules}
        onRulesChange={(rules) => patch({ findRules: rules })}
        anyLabel="Any"
        allLabel="All"
        placeholder="e.g. hot work"
      />

      <RuleGroup
        title="Exempt"
        hint="then remove pages that match"
        logic={draft.exemptRuleLogic}
        onLogicChange={(value) => patch({ exemptRuleLogic: value })}
        rules={draft.exemptRules}
        onRulesChange={(rules) => patch({ exemptRules: rules })}
        anyLabel="Any"
        allLabel="All"
        placeholder="e.g. appendix"
      />

      <div className="ts-builder-actions">
        <button
          type="button"
          className="ts-btn ts-btn-primary"
          onClick={onRun}
          disabled={running || !canRun}
        >
          {running ? 'Searching…' : 'Run'}
        </button>
        {extraActions}
      </div>
    </div>
  )
}

export default RuleBuilder
